import type { LocalOption } from '@/i18n/types';
import {
  SYSTEM_LOCALES,
  systemLocaleEnum,
  type SystemLocale,
} from '@ts-max/car-export-enum-sdk';
import arSA from './ar-SA';
import deDE from './de-DE';
import enUS from './en-US';
import esES from './es-ES';
import frFR from './fr-FR';
import itIT from './it-IT';
import ptBR from './pt-BR';
import ruRU from './ru-RU';
import zhCN from './zh-CN';

type LocaleResourceShape<T> = {
  [Key in keyof T]: T[Key] extends string
    ? string
    : LocaleResourceShape<T[Key]>;
};

const typedZhCN: LocaleResourceShape<typeof enUS> = zhCN;
const typedEnUS: LocaleResourceShape<typeof zhCN> = enUS;
const typedRuRU: LocaleResourceShape<typeof zhCN> = ruRU;
export type Resources = typeof typedZhCN;
type ScopedLocaleResources = {
  [Key in keyof Resources]?: LocaleResourceShape<Resources[Key]>;
};

const typedArSA: ScopedLocaleResources = arSA;
const typedFrFR: ScopedLocaleResources = frFR;
const typedEsES: ScopedLocaleResources = esES;
const typedPtBR: ScopedLocaleResources = ptBR;
const typedDeDE: ScopedLocaleResources = deDE;
const typedItIT: ScopedLocaleResources = itIT;

/** Locales with complete resources for every Mobile route. */
export const FULL_MOBILE_UI_LOCALES = [
  systemLocaleEnum.ZH_CN,
  systemLocaleEnum.EN_US,
  systemLocaleEnum.RU_RU,
] as const;
export type FullMobileUiLocale = (typeof FULL_MOBILE_UI_LOCALES)[number];

// 项目中支持的语言选项
export const resources: Record<SystemLocale, ScopedLocaleResources> = {
  'zh-CN': typedZhCN,
  'en-US': typedEnUS,
  'ru-RU': typedRuRU,
  'ar-SA': typedArSA,
  'fr-FR': typedFrFR,
  'es-ES': typedEsES,
  'pt-BR': typedPtBR,
  'de-DE': typedDeDE,
  'it-IT': typedItIT,
};

// 语言资源类型
// 默认命名空间
export const defaultNS = 'common';

// 项目中支持的语言选项
const localeLabelMap = {
  'zh-CN': '🇨🇳 简体中文',
  'en-US': '🇺🇸 English',
  'ru-RU': '🇷🇺 Русский',
  'ar-SA': '🇸🇦 العربية',
  'fr-FR': '🇫🇷 Français',
  'es-ES': '🇪🇸 Español',
  'pt-BR': '🇧🇷 Português (Brasil)',
  'de-DE': '🇩🇪 Deutsch',
  'it-IT': '🇮🇹 Italiano',
} as const satisfies Record<SystemLocale, string>;

export const localeOptions: LocalOption[] = SYSTEM_LOCALES.map((value) => ({
  value,
  label: localeLabelMap[value],
}));

export const fullMobileUiLocaleOptions: LocalOption[] =
  FULL_MOBILE_UI_LOCALES.map((value) => ({
    value,
    label: localeLabelMap[value],
  }));

// 生成 "ns:key" 格式的所有合法国际化 key，与 t() 函数保持一致
export type I18nKey = {
  [NS in keyof Resources]: `${NS & string}:${Extract<keyof Resources[NS], string>}`;
}[keyof Resources];
